import React, { useState, useRef, useEffect } from 'react';
import { Stage, Layer } from 'react-konva';
import { v4 as uuidv4 } from 'uuid';
import Court from './Court';
import Player from './Player';
import Ball from './Ball';
import AssetsBar from './AssetsBar';
import ActionLayer from './ActionLayer';
import DanmakuLayer from './DanmakuLayer';
import PlayerInfoPanel from './PlayerInfoPanel';
import { BoardEntity, ViewMode, Player as PlayerType, Ball as BallType, TeamType, Action, ActionType, Position, OffensiveRoleCode } from '../../types';
import { COURT_WIDTH, COURT_HEIGHT, APP_BACKGROUND, POSITIONS } from '../../utils/constants';
import { Button, Tooltip, Menu, Dropdown, Slider, message, Modal, List, Card, Tag, Spin, Input, Avatar, Form, Select, Row, Col, Upload, Space, Radio, ConfigProvider, theme } from 'antd';
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { 
  ExpandOutlined, 
  CompressOutlined, 
  DeleteOutlined, 
  FullscreenOutlined,
  EditOutlined,
  CaretRightOutlined,
  PauseOutlined,
  StepForwardOutlined,
  StepBackwardOutlined,
  PlusOutlined,
  UndoOutlined,
  SaveOutlined,
  ScissorOutlined,
  CameraOutlined,
  LinkOutlined,
  PlayCircleOutlined,
  DownloadOutlined,
  RetweetOutlined,
  LineChartOutlined,
  StopOutlined,
  FileTextOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  UserOutlined,
  SearchOutlined,
  ReloadOutlined,
  RobotOutlined,
  FolderAddOutlined
} from '@ant-design/icons';

import GhostDefenseLayer from './GhostDefenseLayer';
import TacticsLibrary from './TacticsLibrary';
import LineupDiagnosticPanel from './LineupDiagnosticPanel';
import { resolveCollisions, calculateGhostDefender } from '../../utils/playerUtils';
import { API_ENDPOINTS } from '../../config/api';

import { ATOMIC_ACTIONS, ATOMIC_ACTION_TAG_OPTIONS } from '../../config/atomicActions';
import { OFFENSIVE_ROLE_TAG_OPTIONS } from '../../config/playerRoles';
import { deriveActionTag, refineFrameActionTags } from '../../utils/actionTagging';
import { computeRosterFit } from '../../utils/rosterFit';
import { FitError } from '../../types';
import RosterFitPanel from './RosterFitPanel';
import { BookOutlined, RadarChartOutlined } from '@ant-design/icons';

interface Frame {
  id: string;
  entitiesMap: Record<ViewMode, BoardEntity[]>;
  actionsMap: Record<ViewMode, Action[]>;
  description?: string;
}

const TacticsBoard: React.FC = () => {
  // Animation State
  const [frames, setFrames] = useState<Frame[]>([{ 
    id: '1', 
    entitiesMap: { full: [], half: [] }, 
    actionsMap: { full: [], half: [] } 
  }]);
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // 1x speed
  const [animationProgress, setAnimationProgress] = useState(0); // 0 to 1 for interpolation
  const [isAnimationMode, setIsAnimationMode] = useState(false); // New state for UI mode

  const [entitiesMap, setEntitiesMap] = useState<Record<ViewMode, BoardEntity[]>>({
    full: [],
    half: []
  });

  // Ref to track entitiesMap for event handlers without triggering re-renders
  const entitiesMapRef = useRef(entitiesMap);
  useEffect(() => {
    entitiesMapRef.current = entitiesMap;
  }, [entitiesMap]);

  const [actionsMap, setActionsMap] = useState<Record<ViewMode, Action[]>>({
    full: [],
    half: []
  });
  const [viewMode, setViewMode] = useState<ViewMode>('full');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  const [activeTool, setActiveTool] = useState<ActionType | null>(null);
  const [isActionMenuCollapsed, setIsActionMenuCollapsed] = useState(false);
  const [currentAction, setCurrentAction] = useState<Action | null>(null);
  
  const stageRef = useRef<any>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();
  const isSwitchingFrame = useRef(false); // Ref to track frame switching to prevent race conditions in useEffect
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<BlobPart[]>([]);

  // Ghost Defense State
  const [showGhostDefense, setShowGhostDefense] = useState(false);

  // AI Chat Panel State
  const [isDiagnosticOpen, setIsDiagnosticOpen] = useState(false);

  // Tactics Library State
  const [isTacticsLibraryVisible, setIsTacticsLibraryVisible] = useState(false);
  const [currentTacticDescription, setCurrentTacticDescription] = useState<string>('');
  
  // Drawer state for Team Roster
  const [isRosterCollapsed, setIsRosterCollapsed] = useState(false);
  
  // Save Tactic State
  const [isSaveModalVisible, setIsSaveModalVisible] = useState(false);
  const [saveForm] = Form.useForm();
  const [saveLoading, setSaveLoading] = useState(false);
  const [savePreviewImage, setSavePreviewImage] = useState<string>('');
  const atomicActionTagOptions = ATOMIC_ACTION_TAG_OPTIONS;
  // Edit Mode State
  const [currentTacticId, setCurrentTacticId] = useState<string | null>(null);
  const [currentTacticMetadata, setCurrentTacticMetadata] = useState<any>(null);


  // Player Tag Assignment State (new)
  const [isTagModalVisible, setIsTagModalVisible] = useState(false);
  const [targetPlayerId, setTargetPlayerId] = useState<string | null>(null);

  /* --- NBA Player Assignment (commented out, kept for reference) ---
  const [isPlayerSearchModalVisible, setIsPlayerSearchModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  --- end NBA --- */

  // 实时自动升降级动作标签 (例如: Isolation <-> PnR_BH)
  React.useEffect(() => {
    if (isPlaying || Object.keys(actionsMap).length === 0) return;
    
    // We only process the active viewMode
    const currentActions = actionsMap[viewMode];
    if (!currentActions || currentActions.length === 0) return;

    // construct hasBallMap dynamically for current actions
    const hasBallMap = new Map<string, boolean>();
    currentActions.forEach(a => {
      const isBallAction = (a.type === 'shoot' || a.type === 'dribble' || a.type === 'pass');
      if (isBallAction) {
         hasBallMap.set(a.playerId, true);
      } else {
         const player = entitiesMap[viewMode]?.find(e => e.id === a.playerId);
         const hb = player ? entitiesMap[viewMode].some(e => e.type === 'ball' && (e as BallType).ownerId === player.id) : false;
         if (hb) hasBallMap.set(a.playerId, true);
      }
    });

    const refined = refineFrameActionTags(currentActions, hasBallMap);
    
    // Check if any tags actually changed to avoid infinite state update loop
    let changed = false;
    if (refined.length !== currentActions.length) changed = true;
    else {
      for(let i = 0; i < refined.length; i++) {
         if (refined[i].actionTag !== currentActions[i].actionTag) {
           changed = true;
           break;
         }
      }
    }

    if (changed) {
      setActionsMap(prev => ({
        ...prev,
        [viewMode]: refined
      }));
    }
  }, [actionsMap, entitiesMap, viewMode, isPlaying]);

  // Sync current state to current frame when it changes
  React.useEffect(() => {
    if (isPlaying) return; // Don't update frames during playback interpolation
    
    // If we are switching frames, do NOT sync the (potentially stale) state back to the frames array
    if (isSwitchingFrame.current) {
      isSwitchingFrame.current = false;
      return;
    }

    setFrames(prev => {
      const newFrames = [...prev];
      // Safety check
      if (!newFrames[currentFrameIndex]) return prev;

      newFrames[currentFrameIndex] = {
        ...newFrames[currentFrameIndex],
        entitiesMap,
        actionsMap
      };
      return newFrames;
    });
  }, [entitiesMap, actionsMap, currentFrameIndex, isPlaying]);

  // Animation Loop
  React.useEffect(() => {
    if (isPlaying) {
      let lastTime = performance.now();
      
      const animate = (time: number) => {
        const dt = (time - lastTime) / 1000; // seconds
        lastTime = time;
        
        setAnimationProgress(prev => {
          const next = prev + dt * playbackSpeed;
          if (next >= 1) {
            // Move to next frame
            if (currentFrameIndex < frames.length - 1) {
              const nextIndex = currentFrameIndex + 1;
              setCurrentFrameIndex(nextIndex);
              setEntitiesMap(frames[nextIndex].entitiesMap);
              setActionsMap(frames[nextIndex].actionsMap);
              return 0;
            } else {
              // End of animation
              setIsPlaying(false);
              setIsAnimationMode(false); // Exit animation mode when done
              
              // Stop recording if active
              if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
              }
              return 0;
            }
          }
          return next;
        });
        
        animationRef.current = requestAnimationFrame(animate);
      };
      
      animationRef.current = requestAnimationFrame(animate);
    } else {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    }
    
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPlaying, playbackSpeed, currentFrameIndex, frames.length]);

  // Catmull-Rom Spline Interpolation Helper
  const getPointOnSpline = (points: Position[], t: number): Position => {
    if (!points || points.length === 0) return { x: 0, y: 0 };
    if (points.length === 1) return points[0];
    
    if (points.length === 2) {
      return {
        x: points[0].x + (points[1].x - points[0].x) * t,
        y: points[0].y + (points[1].y - points[0].y) * t
      };
    }

    const totalSegments = points.length - 1;
    const segmentT = t * totalSegments;
    const index = Math.floor(segmentT);
    const localT = segmentT - index;
    
    const i = Math.min(Math.max(index, 0), totalSegments - 1);
    
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2 >= points.length ? points.length - 1 : i + 2];
    
    const tt = localT * localT;
    const ttt = tt * localT;
    
    const x = 0.5 * (
      (2 * p1.x) +
      (-p0.x + p2.x) * localT +
      (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * tt +
      (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * ttt
    );
    
    const y = 0.5 * (
      (2 * p1.y) +
      (-p0.y + p2.y) * localT +
      (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * tt +
      (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * ttt
    );
    
    return { x, y };
  };

  // Actions for current viewMode (MUST be before getRenderEntities)
  const actions = actionsMap[viewMode];

  // Interpolated Entities for Rendering
  const getRenderEntities = () => {
    // If not in animation mode, or if we are at the very end of the animation sequence
    if (!isAnimationMode || currentFrameIndex >= frames.length - 1) {
      return entitiesMap[viewMode];
    }

    const currentFrame = frames[currentFrameIndex];
    const nextFrame = frames[currentFrameIndex + 1];
    
    const currentEntities = currentFrame.entitiesMap[viewMode];
    const nextEntities = nextFrame.entitiesMap[viewMode];
    const currentActions = currentFrame.actionsMap[viewMode];

    let renderedEntities = currentEntities.map(entity => {
      const nextEntity = nextEntities.find(e => e.id === entity.id);
      if (!nextEntity) return entity;

      let pos = { ...entity.position };
      let rot = entity.type === 'player' ? (entity as PlayerType).rotation : 0;
      let scale = 1;
      let armExtension = 0;
      let actionType: ActionType | undefined;

      // Check for Action-based movement
      let action: Action | undefined;
      
      if (entity.type === 'player') {
         action = currentActions.find(a => a.playerId === entity.id && ['move', 'dribble', 'screen', 'steal', 'block'].includes(a.type));
         actionType = action?.type;
         
         // Handle Block Jump Animation
         if (action && action.type === 'block') {
             // Parabolic jump: 4 * t * (1 - t) peaks at 1 when t=0.5
             // We want a scale increase, e.g., from 1 to 1.3
             const jumpHeight = 0.3;
             scale = 1 + jumpHeight * (4 * animationProgress * (1 - animationProgress));
         }

         // Handle Steal Animation (Arm Extension + Fast Movement)
         if (action && action.type === 'steal') {
             // Logic moved down to use local 't'
         }

      } else if (entity.type === 'ball') {
         const ball = entity as BallType;
         if (ball.ownerId) {
           // 1. Check for Pass/Shoot (Ball follows its own path independent of player)
           action = currentActions.find(a => a.playerId === ball.ownerId && (a.type === 'pass' || a.type === 'shoot'));
           
           // 2. If not passing/shooting, Ball MUST follow Owner's movement (Dribble/Move/Steal)
           // This ensures the ball respects the owner's speed/acceleration settings
           if (!action) {
               const ownerAction = currentActions.find(a => a.playerId === ball.ownerId && ['move', 'dribble', 'steal', 'screen', 'block'].includes(a.type));
               if (ownerAction) {
                   action = ownerAction;
               }
           }
         }
      }

      if (action && action.path.length >= 2) {
         // Determine interpolation 't'
         let t = animationProgress;
         
         // Apply Speed Multiplier
         // Walk: 1.0x (Base speed - uses full frame time to reach destination)
         // Jog: 1.5x (Normal - finishes at ~66% of frame time)
         // Sprint: 2.5x (Fast - finishes at ~40% of frame time)
         
         let speedMultiplier = 1.5; // Default (Jog)
         if (action.speed === 'walk') speedMultiplier = 1.0;
         if (action.speed === 'sprint') speedMultiplier = 2.5;
         
         // Calculate local progress based on speed
         let localProgress = Math.min(1, animationProgress * speedMultiplier);
         
         t = localProgress;

         // For Steal, use Ease-In (Acceleration) to simulate burst speed
         if (action.type === 'steal') {
             t = t * t; // Quadratic Ease-In
         }
         
         // Update Arm Extension based on t (local progress)
         if (action.type === 'steal') {
             if (t > 0.3) {
                 armExtension = (t - 0.3) / 0.7;
             }
             scale = 1 + 0.1 * t;
         }

         // Use Spline Interpolation
         // FIX: If t >= 1, explicitly use the last point to avoid spline calculation errors
         if (t >= 1) {
             pos = action.path[action.path.length - 1];
         } else {
             pos = getPointOnSpline(action.path, t);
         }
         
         // Rotation for players
         if (entity.type === 'player' && nextEntity.type === 'player') {
            const p1 = entity as PlayerType;
            const p2 = nextEntity as PlayerType;
            if (p1.rotation !== undefined && p2.rotation !== undefined) {
              // Rotation should follow shortest path
              let delta = p2.rotation - p1.rotation;
              if (delta > 180) delta -= 360;
              if (delta < -180) delta += 360;
              rot = p1.rotation + delta * t;
            }
         }
      } else {
         // Linear Interpolation (Fallback)
         pos = {
          x: entity.position.x + (nextEntity.position.x - entity.position.x) * animationProgress,
          y: entity.position.y + (nextEntity.position.y - entity.position.y) * animationProgress
        };
        
        if (entity.type === 'player' && nextEntity.type === 'player') {
            const p1 = entity as PlayerType;
            const p2 = nextEntity as PlayerType;
            if (p1.rotation !== undefined && p2.rotation !== undefined) {
              let delta = p2.rotation - p1.rotation;
              if (delta > 180) delta -= 360;
              if (delta < -180) delta += 360;
              rot = p1.rotation + delta * animationProgress;
            }
        }
      }

      return {
        ...entity,
        position: pos,
        rotation: rot,
        scale: scale,
        armExtension: armExtension,
        actionType: actionType
      };
    });

    const players = renderedEntities.filter(e => e.type === 'player') as PlayerType[];
    const ball = renderedEntities.find(e => e.type === 'ball') as BallType | undefined;

    // --- Collision Resolution for Animation ---
    if (isPlaying && ball) {

      // Generate Ghost Defenders as Obstacles
      const ghostObstacles = players.map(p => {
          const { position, radius } = calculateGhostDefender(p, ball, viewMode, players);
          return { position, radius };
      });

      // Multi-pass resolution for stability
      for (let i = 0; i < 2; i++) {
        players.forEach(p => {
          const { x, y } = resolveCollisions(p.id, p.position.x, p.position.y, players, ghostObstacles);
          p.position.x = x;
          p.position.y = y;
        });
      }

    }

    // Keep ball offset from owner's center in both play and pause states while animating
    // so the ball doesn't cover the holder's position label (PG/SG/SF/PF/C).
    if (ball && ball.ownerId) {
      // Pass/Shoot logic: Ball moves independently of player while playing.
      const ballAction = actions.find(a => a.playerId === ball.ownerId && (a.type === 'pass' || a.type === 'shoot'));
      if (ballAction && ballAction.path.length >= 2 && isPlaying) {
        // Interpolate ball movement
        let t = animationProgress;
        let speedMultiplier = 1.5;
        if (ballAction.speed === 'walk') speedMultiplier = 1.0;
        if (ballAction.speed === 'sprint') speedMultiplier = 2.5;
        let localProgress = Math.min(1, animationProgress * speedMultiplier);
        t = localProgress;
        if (t >= 1) {
          ball.position = ballAction.path[ballAction.path.length - 1];
        } else {
          ball.position = getPointOnSpline(ballAction.path, t);
        }
      } else {
        // Ball following owner logic
        const owner = renderedEntities.find(e => e.id === ball.ownerId) as PlayerType | undefined;
        if (owner) {
          // Preserve authored left/right dribble hand from the current frame.
          const sourceOwner = currentEntities.find(e => e.id === owner.id) as PlayerType | undefined;
          const sourceBall = currentEntities.find(e => e.type === 'ball' && e.id === ball.id) as BallType | undefined;

          const baseRotation = (sourceOwner?.rotation ?? owner.rotation ?? 0);
          const baseRad = (baseRotation * Math.PI) / 180;
          let handSign: 1 | -1 = 1; // +1 = right hand, -1 = left hand

          if (sourceOwner && sourceBall) {
            const baseDx = sourceBall.position.x - sourceOwner.position.x;
            const baseDy = sourceBall.position.y - sourceOwner.position.y;
            const localX = baseDx * Math.cos(baseRad) + baseDy * Math.sin(baseRad);
            if (Math.abs(localX) > 0.001) {
              handSign = localX >= 0 ? 1 : -1;
            }
          }

          // Place the ball in local player space, then rotate to world space.
          // localY < 0 keeps the ball slightly in front to avoid covering the jersey label.
          const localOffsetX = handSign * 13;
          const localOffsetY = -17;
          const rotation = owner.rotation || 0;
          const rotationRad = (rotation * Math.PI) / 180;
          const worldOffsetX = localOffsetX * Math.cos(rotationRad) - localOffsetY * Math.sin(rotationRad);
          const worldOffsetY = localOffsetX * Math.sin(rotationRad) + localOffsetY * Math.cos(rotationRad);

          ball.position = {
            x: owner.position.x + worldOffsetX,
            y: owner.position.y + worldOffsetY
          };
        }
      }
    }

    return renderedEntities;
  };

  const renderEntities = getRenderEntities();
  const entities = renderEntities; // Override for rendering

  const isVertical = viewMode === 'half';
  const stageWidth = isVertical ? COURT_HEIGHT : COURT_WIDTH;
  const stageHeight = viewMode === 'half' ? COURT_WIDTH / 2 : COURT_HEIGHT;

  // Get selected entity
  const selectedEntity = entities.find(e => e.id === selectedId);
  const isBallHandler = selectedEntity?.type === 'player' && 
    entities.some(e => e.type === 'ball' && (e as BallType).ownerId === selectedEntity.id);

  // Get selected action
  const selectedAction = actions.find(a => a.id === selectedActionId);

  // Handle dragging entities on the board
  const handleEntityDrag = React.useCallback((id: string, x: number, y: number) => {
    // If we are in drawing mode, do NOT move the entity
    if (activeTool) return;

    // Boundary Check
    const maxX = viewMode === 'half' ? COURT_WIDTH / 2 : COURT_WIDTH;
    const maxY = COURT_HEIGHT;
    
    // Clamp values
    const clampedX = Math.max(0, Math.min(x, maxX));
    const clampedY = Math.max(0, Math.min(y, maxY));

    // Calculate delta using ref to avoid dependency on entitiesMap
    const currentEntities = entitiesMapRef.current[viewMode];
    const oldEntity = currentEntities.find(e => e.id === id);
    
    if (oldEntity) {
        const dx = clampedX - oldEntity.position.x;
        const dy = clampedY - oldEntity.position.y;

        // Update Actions if it's a player
        if (oldEntity.type === 'player' && (dx !== 0 || dy !== 0)) {
            setActionsMap(prev => {
                const currentActions = prev[viewMode];
                const updatedActions = currentActions.map(a => {
                    if (a.playerId === id) {
                        return {
                            ...a,
                            path: a.path.map(p => ({ x: p.x + dx, y: p.y + dy }))
                        };
                    }
                    return a;
                });
                return { ...prev, [viewMode]: updatedActions };
            });
        }
    }

    setEntitiesMap(prev => {
      const currentEntities = prev[viewMode];
      const entity = currentEntities.find(e => e.id === id);
      
      if (!entity) return prev;

      let updatedEntities = currentEntities.map(e => e.id === id ? { ...e, position: { x: clampedX, y: clampedY } } : e);

      // Logic for Ball Handler (Magnetic Ball)
      if (entity.type === 'player') {
        // If moving a player, check if they have the ball
        // If yes, move the ball with them
        const ball = updatedEntities.find(e => e.type === 'ball' && (e as BallType).ownerId === id) as BallType | undefined;
        if (ball) {
          // Calculate delta using clamped values
          const dx = clampedX - entity.position.x;
          const dy = clampedY - entity.position.y;
          
          updatedEntities = updatedEntities.map(e => {
            if (e.id === ball.id) {
              return {
                ...e,
                position: {
                  x: e.position.x + dx,
                  y: e.position.y + dy
                }
              };
            }
            return e;
          });
        }
      } else if (entity.type === 'ball') {
        // If moving the ball, check for proximity to players to attach
        // Threshold distance to snap to player (e.g. 30px)
        const SNAP_DISTANCE = 30;
        let nearestPlayerId: string | undefined;
        let minDistance = Infinity;

        updatedEntities.forEach(e => {
          if (e.type === 'player') {
            const dist = Math.sqrt(Math.pow(e.position.x - clampedX, 2) + Math.pow(e.position.y - clampedY, 2));
            if (dist < minDistance) {
              minDistance = dist;
              nearestPlayerId = e.id;
            }
          }
        });

        if (nearestPlayerId && minDistance < SNAP_DISTANCE) {
          // Snap to player
          // We can optionally snap the position visually too, or just set the owner
          // Let's set the owner.
          updatedEntities = updatedEntities.map(e => {
            if (e.id === id) {
              return { ...e, ownerId: nearestPlayerId };
            }
            return e;
          });
        } else {
          // Release ownership if dragged away
          updatedEntities = updatedEntities.map(e => {
            if (e.id === id) {
              const ball = e as BallType;
              // Only clear if it WAS owned
              if (ball.ownerId) {
                 return { ...e, ownerId: undefined };
              }
            }
            return e;
          });
        }
      }

      return {
        ...prev,
        [viewMode]: updatedEntities
      };
    });
  }, [viewMode]);

  const [throttledTime, setThrottledTime] = useState(0);
  const lastChartUpdateRef = useRef(0);

  // ─── Roster-Fit live error computation ──────────────────────────────────────
  // Temporarily disabled: action-line-based warning computation
  const fitErrors: FitError[] = React.useMemo(() => [], [actionsMap, entitiesMap, viewMode, currentFrameIndex]);
  // const fitErrors: FitError[] = React.useMemo(
  //   () => computeRosterFit(actionsMap[viewMode], entitiesMap[viewMode], currentFrameIndex),
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  //   [actionsMap, entitiesMap, viewMode, currentFrameIndex],
  // );

  // Memoize Court to prevent re-renders during animation
  const memoizedCourt = React.useMemo(() => <Court viewMode={viewMode} />, [viewMode]);

  

  // Handle rotating entities
  const handleEntityRotate = React.useCallback((id: string, rotation: number) => {
    setEntitiesMap(prev => ({
      ...prev,
      [viewMode]: prev[viewMode].map(e => e.id === id ? { ...e, rotation } : e)
    }));
  }, [viewMode]);

  // Handle stage click to deselect
  const handleStageClick = (e: any) => {
    // If drawing, finish drawing
    if (activeTool && currentAction) {
      setActionsMap(prev => ({
        ...prev,
        [viewMode]: [...prev[viewMode], currentAction]
      }));
      // Select the newly created action
      setSelectedActionId(currentAction.id);
      setSelectedId(null); // Deselect player
      setCurrentAction(null);
      setActiveTool(null);
      return;
    }

    // If the click target is the Stage itself (empty space)
    if (e.target && e.target.getStage && e.target === e.target.getStage()) {
      setSelectedId(null);
      setSelectedActionId(null);
      setActiveTool(null);
      return;
    }

    // If the click target is NOT a part of a Player or Ball group
    // We can check the parent chain.
    let parent = e.target && e.target.getParent ? e.target.getParent() : null;
    let isEntity = false;
    while (parent) {
      if (parent.attrs.name === 'entity-group') {
        isEntity = true;
        break;
      }
      parent = parent.getParent ? parent.getParent() : null;
    }

    if (!isEntity) {
      // Let's check if we clicked the court background
      const name = e.target && e.target.attrs ? e.target.attrs.name : null;
      const parentName = (e.target && e.target.getParent && e.target.getParent())?.attrs?.name;

      if (name === 'court-background' || name === 'court-base' || parentName === 'wood-texture') {
         setSelectedId(null);
         setSelectedActionId(null);
         setActiveTool(null);
      }
    } else {
      // Clicked an entity
      setSelectedActionId(null); // Deselect action
    }
  };

  // Handle Action Selection
  const handleActionSelect = (id: string) => {
    setSelectedActionId(id);
    setSelectedId(null); // Deselect player
    setActiveTool(null);
    setIsActionMenuCollapsed(false); // Always expand when newly selected
  };

  // Delete Entity
  const handleDeleteEntity = () => {
    if (selectedId) {
      setEntitiesMap(prev => {
        const currentEntities = prev[viewMode];
        // Filter out the deleted entity
        const filteredEntities = currentEntities.filter(e => e.id !== selectedId);
        
        // If we deleted a player, check if they had the ball and release it
        const updatedEntities = filteredEntities.map(e => {
          if (e.type === 'ball' && (e as BallType).ownerId === selectedId) {
            return { ...e, ownerId: undefined };
          }
          return e;
        });

        return {
          ...prev,
          [viewMode]: updatedEntities
        };
      });
      
      // Also delete associated actions
      setActionsMap(prev => ({
        ...prev,
        [viewMode]: prev[viewMode].filter(a => a.playerId !== selectedId)
      }));
      setSelectedId(null);
    }
  };

  // Delete Action
  const handleDeleteAction = () => {
    if (selectedActionId) {
      setActionsMap(prev => ({
        ...prev,
        [viewMode]: prev[viewMode].filter(a => a.id !== selectedActionId)
      }));
      setSelectedActionId(null);
    }
  };

  // Change Action Type
  const handleChangeActionType = (type: ActionType) => {
    if (selectedActionId) {
      setActionsMap(prev => ({
        ...prev,
        [viewMode]: prev[viewMode].map(a => {
          if (a.id !== selectedActionId) return a;
          const currentEntities = entitiesMapRef.current[viewMode];
          const ap = currentEntities.find(e => e.id === a.playerId) as PlayerType | undefined;
          const hb = ap ? currentEntities.some(e => e.type === 'ball' && (e as BallType).ownerId === ap.id) : false;
          const startPos = a.path[0];
          const endPos = a.path[a.path.length - 1] ?? startPos;
          return { ...a, type, actionTag: deriveActionTag(type, hb, startPos.x, startPos.y, endPos.x, endPos.y) };
        }),
      }));
    }
  };

  // Handle Action Point Drag
  const handleActionPointChange = (actionId: string, index: number, newPos: {x: number, y: number}) => {
    setActionsMap(prev => ({
      ...prev,
      [viewMode]: prev[viewMode].map(a => {
        if (a.id === actionId) {
          const newPath = [...a.path];
          newPath[index] = newPos;
          return { ...a, path: newPath };
        }
        return a;
      })
    }));
  };

  // Handle Mouse Down for Drawing
  const handleStageMouseDown = (e: any) => {
    if (!activeTool || !selectedId) return;
    
    const stage = e.target && e.target.getStage ? e.target.getStage() : null;
    if (!stage) return;
    const pos = stage.getPointerPosition();
    if (!pos) return;

    let layerPos = pos;
    if (isVertical) {
       const layer = stageRef.current.findOne('Layer');
       const transform = layer.getAbsoluteTransform().copy();
       transform.invert();
       layerPos = transform.point(pos);
    }

    const startPos = entities.find(e => e.id === selectedId)?.position || layerPos;
    
    setCurrentAction({
      id: uuidv4(),
      type: activeTool,
      playerId: selectedId,
      path: [startPos, layerPos] // Start with 2 points
    });
  };

  // Handle Mouse Move for Drawing
  const handleStageMouseMove = (e: any) => {
    if (!activeTool || !currentAction) return;
    
    const stage = e.target && e.target.getStage ? e.target.getStage() : null;
    if (!stage) return;
    const pos = stage.getPointerPosition();
    if (!pos) return;

    let layerPos = pos;
    if (isVertical) {
       const layer = stageRef.current.findOne('Layer');
       const transform = layer.getAbsoluteTransform().copy();
       transform.invert();
       layerPos = transform.point(pos);
    }

    const newPath = [...currentAction.path];
    newPath[newPath.length - 1] = layerPos;

    setCurrentAction(prev => prev ? { ...prev, path: newPath } : null);
  };

  // Handle Mouse Up for Drawing
  const handleStageMouseUp = (e: any) => {
    if (!activeTool || !currentAction) return;
    
    // Finalize action
    // Insert two midpoints for better curving
    const start = currentAction.path[0];
    const end = currentAction.path[currentAction.path.length - 1];
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    
    const p1 = { x: start.x + dx * 0.33, y: start.y + dy * 0.33 };
    const p2 = { x: start.x + dx * 0.66, y: start.y + dy * 0.66 };
    
    const currentEntities = entitiesMapRef.current[viewMode];
    const actingPlayer = currentEntities.find(e => e.id === currentAction.playerId) as PlayerType | undefined;
    const hasBallNow = actingPlayer
      ? currentEntities.some(e => e.type === 'ball' && (e as BallType).ownerId === actingPlayer.id)
      : false;

    const finalAction = {
      ...currentAction,
      path: [start, p1, p2, end],
      color: '#ff4d4f', // Default red
      actionTag: deriveActionTag(currentAction.type, hasBallNow, start.x, start.y, end.x, end.y),
    };

    setActionsMap(prev => ({
      ...prev,
      [viewMode]: [...prev[viewMode], finalAction]
    }));
    
    setSelectedActionId(finalAction.id);
    setSelectedId(null);
    setCurrentAction(null);
    setActiveTool(null);
  };

  // Calculate next number for a team
  const getNextNumber = (team: TeamType, currentEntities: BoardEntity[]): string => {
    const teamPlayers = currentEntities.filter(
      e => e.type === 'player' && (e as PlayerType).team === team
    ) as PlayerType[];
    
    const existingNumbers = new Set(
      teamPlayers
        .map(p => parseInt(p.number))
        .filter(n => !isNaN(n))
    );

    // Find first available number starting from 1
    let num = 1;
    while (existingNumbers.has(num)) {
      num++;
    }
    
    return num.toString();
  };

  // Handle dropping new assets onto the board
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;

    stage.setPointersPositions(e);
    const pointerPosition = stage.getPointerPosition();
    
    if (!pointerPosition) return;

    const type = e.dataTransfer.getData('type');
    const dataString = e.dataTransfer.getData('data');
    
    // Transform pointer position if in vertical mode (rotated 90 degrees)
    let x = pointerPosition.x;
    let y = pointerPosition.y;

    if (isVertical) {
      // Inverse transform for 90 degree rotation
      // Visual: x' = -y + offset, y' = x
      // Inverse: x = y', y = -(x' - offset) = offset - x'
      // offset is stageWidth (which is COURT_HEIGHT)
      const offset = stageWidth;
      const tempX = x;
      x = y;
      y = offset - tempX;
    }

    let newEntity: BoardEntity;

    if (type === 'ball') {
      newEntity = {
        id: uuidv4(),
        type: 'ball',
        position: { x, y }
      };
    } else if (type === 'player') {
      const data = JSON.parse(dataString);
      const nextNumber = data.number || getNextNumber(data.team, entities);
      
      newEntity = {
        id: uuidv4(),
        type: 'player',
        team: data.team,
        number: nextNumber,
        position: { x, y },
        rotation: 0
      };
    } else {
      return;
    }

    setEntitiesMap(prev => ({
      ...prev,
      [viewMode]: [...prev[viewMode], newEntity]
    }));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // Necessary to allow dropping
  };

  const clearBoard = () => {
    // Reset Edit State
    setCurrentTacticId(null);
    setCurrentTacticMetadata(null);
    setCurrentTacticDescription('');
    
    // Clear Board
    setEntitiesMap(prev => ({
      ...prev,
      [viewMode]: []
    }));
    setActionsMap(prev => ({
      ...prev,
      [viewMode]: []
    }));
    
    // Reset Animation Frames
    const initialFrame: Frame = {
        id: uuidv4(),
        entitiesMap: { full: [], half: [] },
        actionsMap: { full: [], half: [] }
    };
    setFrames([initialFrame]);
    setCurrentFrameIndex(0);
    setIsPlaying(false);
  };

  // CSS transform for perspective view
  const boardStyle: React.CSSProperties = {
    border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
    backgroundColor: '#333',
    transition: 'transform 0.5s ease',
    transformStyle: 'preserve-3d',
    transformOrigin: 'center top', // Pivot from top to keep it in view
    overflow: 'hidden'
  };

  const SidebarButton = ({ icon, onClick, active, tooltip }: any) => (
    <Tooltip title={tooltip} placement="right">
      <Button 
        type={active ? 'primary' : 'default'}
        shape="circle" 
        size="large" 
        icon={icon} 
        onClick={onClick}
        style={{ 
          backgroundColor: active ? '#5C7ABD' : 'transparent', 
          borderColor: active ? '#5C7ABD' : 'rgba(255,255,255,0.3)',
          color: active ? '#fff' : 'rgba(255,255,255,0.7)',
          marginBottom: '15px'
        }}
      />
    </Tooltip>
  );

  const SidebarSectionLabel = ({ text }: { text: string }) => (
    <div style={{ 
      fontSize: '11px', 
      color: '#FFFFFF', 
      fontWeight: 800, 
      letterSpacing: '0.8px', 
      marginBottom: '8px',
      marginTop: '4px',
      width: '100%',
      textAlign: 'center'
    }}>
      {text}
    </div>
  );

  const SidebarDivider = () => (
    <div style={{
      width: '32px',
      height: '1px',
      background: 'rgba(255,255,255,0.1)',
      margin: '0px 0 12px 0'
    }} />
  );

  // Action Menu for Selected Entity (Player or Ball)
  const renderPlayerMenu = () => {
    if (!selectedEntity) return null;
    if (selectedEntity.type !== 'player' && selectedEntity.type !== 'ball') return null;
    // Hide panel while drawing a path so it doesn't cover bezier control points
    if (activeTool) return null;

    // Calculate position relative to the entity
    let x = selectedEntity.position.x;
    let y = selectedEntity.position.y;

    if (isVertical) {
       const tempX = x;
       x = stageWidth - y;
       y = tempX;
    }

    // Boundary detection: If too close to top, flip menu to bottom
    const isTooCloseToTop = y < 120;

    const menuStyle: React.CSSProperties = {
      position: 'absolute',
      top: y,
      left: x,
      zIndex: 100,
      background: 'rgba(35, 35, 35, 0.95)',
      backdropFilter: 'blur(8px)',
      padding: '8px',
      borderRadius: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      // Dynamic positioning
      transform: isTooCloseToTop ? 'translate(-50%, 0)' : 'translate(-50%, -100%)', 
      marginTop: isTooCloseToTop ? '45px' : '-55px', // Below: clear player (approx 20px radius + 25px). Above: clear rotation handle.
      border: '1px solid rgba(255,255,255,0.1)'
    };

    return (
      <div style={menuStyle}>
        {selectedEntity.type === 'player' && (
          <Button 
            size="small"
            shape="round"
            icon={<EditOutlined />} 
            onClick={() => setActiveTool('move')} // Default to move, then can change
            type={activeTool ? 'primary' : 'text'}
            style={{ color: !activeTool ? 'white' : undefined }}
          >
            Draw Path
          </Button>
        )}
        <Button 
          size="small"
          shape="round"
          type="text"
          danger
          icon={<DeleteOutlined />} 
          onClick={handleDeleteEntity}
        >
          Delete
        </Button>
        
        {/* Little arrow */}
        <div style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            [isTooCloseToTop ? 'top' : 'bottom']: '-6px',
            [isTooCloseToTop ? 'borderBottom' : 'borderTop']: '6px solid rgba(35, 35, 35, 0.95)'
        }} />
      </div>
    );
  };

  // Change Action Color
  const handleChangeActionColor = (color: string) => {
    if (selectedActionId) {
      setActionsMap(prev => ({
        ...prev,
        [viewMode]: prev[viewMode].map(a => a.id === selectedActionId ? { ...a, color } : a)
      }));
    }
  };

  // Change Action Speed
  const handleChangeActionSpeed = (speed: 'walk' | 'jog' | 'sprint') => {
    if (selectedActionId) {
      setActionsMap(prev => ({
        ...prev,
        [viewMode]: prev[viewMode].map(a => a.id === selectedActionId ? { ...a, speed } : a)
      }));
    }
  };

  // Action Menu for Selected Action (Line)
  const renderActionMenu = () => {
    if (!selectedAction) return null;

    // ── Collapsed "pill" state ──────────────────────────────────────────────
    // Helper to transform logical point to screen point (shared)
    const toScreenPt = (p: Position) => {
        let sx = p.x, sy = p.y;
        if (isVertical) { const tmp = sx; sx = stageWidth - sy; sy = tmp; }
        return { x: sx, y: sy };
    };

    const endPoint = selectedAction.path[selectedAction.path.length - 1];
    const endScreen = toScreenPt(endPoint);
    const isTooCloseToTop = endScreen.y < 200;
    const isTooCloseToRight = endScreen.x > stageWidth - 150;

    if (isActionMenuCollapsed) {
      // Render a tiny toggle pill so user can re-expand
      const pillStyle: React.CSSProperties = {
        position: 'absolute',
        top: endScreen.y,
        left: endScreen.x,
        zIndex: 100,
        transform: `translate(${isTooCloseToRight ? '-90%' : '-50%'}, ${isTooCloseToTop ? '0' : '-100%'})`,
        marginTop: isTooCloseToTop ? '45px' : '-45px',
        background: 'rgba(35,35,35,0.85)',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '20px',
        padding: '3px 10px',
        color: 'rgba(255,255,255,0.7)',
        fontSize: '13px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        whiteSpace: 'nowrap',
        userSelect: 'none',
        pointerEvents: 'auto',
        backdropFilter: 'blur(6px)',
      };
      return (
        <div style={pillStyle} onClick={() => setIsActionMenuCollapsed(false)}>
          <span style={{ fontSize: '11px' }}>▶</span>
          <span>Edit Action</span>
        </div>
      );
    }

    // Position at Top-Right of the end point (arrow head)
    // Center on the end point
    let menuX = endScreen.x; 
    let menuY = endScreen.y;

    const menuStyle: React.CSSProperties = {
      position: 'absolute',
      top: menuY,
      left: menuX,
      zIndex: 100,
      background: 'rgba(35, 35, 35, 0.95)',
      backdropFilter: 'blur(8px)',
      padding: '8px',
      borderRadius: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      // Dynamic positioning
      transform: `translate(${isTooCloseToRight ? '-90%' : '-50%'}, ${isTooCloseToTop ? '0' : '-100%'})`,
      marginTop: isTooCloseToTop ? '45px' : '-45px',
      border: '1px solid rgba(255,255,255,0.1)',
      pointerEvents: 'auto' // Ensure clicks pass through
    };

    const rowStyle: React.CSSProperties = {
      display: 'flex',
      gap: '6px',
      justifyContent: 'center'
    };

    // Determine available types based on player role (ball handler or not)
    // We need to find the player associated with this action
    const player = entities.find(e => e.id === selectedAction.playerId) as PlayerType | undefined;
    const hasBall = player && entities.some(e => e.type === 'ball' && (e as BallType).ownerId === player.id);
    
    // Determine if this is a defensive player (not on the same team as the ball owner)
    const ball = entities.find(e => e.type === 'ball') as BallType | undefined;
    const ballOwner = entities.find(e => e.id === ball?.ownerId) as PlayerType | undefined;
    const offenseTeam = ballOwner ? ballOwner.team : 'red'; // Default offense is red
    const isDefense = player && player.team !== offenseTeam;

    const colors = ['#ff4d4f', '#1890ff', '#52c41a', '#faad14', '#722ed1', '#eb2f96', '#ffffff', '#8c8c8c'];
    const currentSpeed = selectedAction.speed || 'jog';

    return (
      <div style={menuStyle}>
        {/* Collapse button row */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '-2px' }}>
          <span
            title="Collapse panel to drag curve points"
            onClick={() => setIsActionMenuCollapsed(true)}
            style={{
              cursor: 'pointer',
              fontSize: '12px',
              color: 'rgba(255,255,255,0.45)',
              padding: '0 2px',
              lineHeight: 1,
              userSelect: 'none',
            }}
          >✕ collapse</span>
        </div>
        <div style={rowStyle}>
          {hasBall ? (
            <>
              <Tooltip title="Dribble"><Button size="small" shape="round" type={selectedAction.type === 'dribble' ? 'primary' : 'text'} style={{ color: selectedAction.type !== 'dribble' ? 'white' : undefined }} onClick={() => handleChangeActionType('dribble')}>Dribble</Button></Tooltip>
              <Tooltip title="Pass"><Button size="small" shape="round" type={selectedAction.type === 'pass' ? 'primary' : 'text'} style={{ color: selectedAction.type !== 'pass' ? 'white' : undefined }} onClick={() => handleChangeActionType('pass')}>Pass</Button></Tooltip>
              <Tooltip title="Shoot"><Button size="small" shape="round" type={selectedAction.type === 'shoot' ? 'primary' : 'text'} style={{ color: selectedAction.type !== 'shoot' ? 'white' : undefined }} onClick={() => handleChangeActionType('shoot')}>Shoot</Button></Tooltip>
            </>
          ) : isDefense ? (
            <>
              <Tooltip title="Move"><Button size="small" shape="round" type={selectedAction.type === 'move' ? 'primary' : 'text'} style={{ color: selectedAction.type !== 'move' ? 'white' : undefined }} onClick={() => handleChangeActionType('move')}>Move</Button></Tooltip>
              <Tooltip title="Steal"><Button size="small" shape="round" type={selectedAction.type === 'steal' ? 'primary' : 'text'} style={{ color: selectedAction.type !== 'steal' ? 'white' : undefined }} onClick={() => handleChangeActionType('steal')}>Steal</Button></Tooltip>
              <Tooltip title="Block"><Button size="small" shape="round" type={selectedAction.type === 'block' ? 'primary' : 'text'} style={{ color: selectedAction.type !== 'block' ? 'white' : undefined }} onClick={() => handleChangeActionType('block')}>Block</Button></Tooltip>
            </>
          ) : (
            <>
              <Tooltip title="Move"><Button size="small" shape="round" type={selectedAction.type === 'move' ? 'primary' : 'text'} style={{ color: selectedAction.type !== 'move' ? 'white' : undefined }} onClick={() => handleChangeActionType('move')}>Move</Button></Tooltip>
              <Tooltip title="Screen"><Button size="small" shape="round" type={selectedAction.type === 'screen' ? 'primary' : 'text'} style={{ color: selectedAction.type !== 'screen' ? 'white' : undefined }} onClick={() => handleChangeActionType('screen')}>Screen</Button></Tooltip>
            </>
          )}
          <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={handleDeleteAction} />
        </div>
        
        {/* Speed Control Row */}
        <div style={{ ...rowStyle, paddingTop: '4px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Tooltip title="Walk (Slow)">
                <Button 
                    size="small" 
                    shape="circle" 
                    type={currentSpeed === 'walk' ? 'primary' : 'text'} 
              style={{ color: currentSpeed !== 'walk' ? 'white' : undefined, fontSize: '13px', fontWeight: 800 }} 
                    onClick={() => handleChangeActionSpeed('walk')}
                >
              {'\uD83D\uDC22'}
                </Button>
            </Tooltip>
            <Tooltip title="Jog (Normal)">
                <Button 
                    size="small" 
                    shape="circle" 
                    type={currentSpeed === 'jog' ? 'primary' : 'text'} 
              style={{ color: currentSpeed !== 'jog' ? 'white' : undefined, fontSize: '13px', fontWeight: 800 }} 
                    onClick={() => handleChangeActionSpeed('jog')}
                >
              {'\uD83C\uDFC3'}
                </Button>
            </Tooltip>
            <Tooltip title="Sprint (Fast)">
                <Button 
                    size="small" 
                    shape="circle" 
                    type={currentSpeed === 'sprint' ? 'primary' : 'text'} 
              style={{ color: currentSpeed !== 'sprint' ? 'white' : undefined, fontSize: '13px', fontWeight: 800 }} 
                    onClick={() => handleChangeActionSpeed('sprint')}
                >
              {'\u26A1'}
                </Button>
            </Tooltip>
        </div>

        {/* Label Input Row (Danmaku) */}
        <div style={{ ...rowStyle, paddingTop: '4px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Input 
                className="styled-dark-input styled-dark-input-round"
                placeholder="Action Label (e.g. Screen)" 
                value={selectedAction.label || ''} 
                onChange={(e) => {
                    const newLabel = e.target.value;
                    setActionsMap(prev => ({
                        ...prev,
                        [viewMode]: prev[viewMode].map(a => a.id === selectedActionId ? { ...a, label: newLabel } : a)
                    }));
                }}
                prefix={<EditOutlined style={{ color: '#FFFFFF', marginRight: '4px' }} />}
                style={{ width: '100%' }}
                onClick={(e) => e.stopPropagation()} 
            />
        </div>

        <div style={{ ...rowStyle, paddingTop: '4px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          {colors.map(c => (
            <div 
              key={c}
              onClick={() => handleChangeActionColor(c)}
              style={{
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                backgroundColor: c,
                cursor: 'pointer',
                border: selectedAction.color === c ? '2px solid white' : '2px solid transparent',
                boxShadow: selectedAction.color === c ? '0 0 8px rgba(255,255,255,0.5)' : 'none',
                transition: 'all 0.2s'
              }}
            />
          ))}
        </div>
        {/* Little arrow */}
        <div style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            [isTooCloseToTop ? 'top' : 'bottom']: '-6px',
            [isTooCloseToTop ? 'borderBottom' : 'borderTop']: '6px solid rgba(35, 35, 35, 0.95)'
        }} />
      </div>
    );
  };

  // Frame Management
  const handleAddFrame = () => {
    // Check if current frame has any actions
    const hasActions = actionsMap.full.length > 0 || actionsMap.half.length > 0;
    if (!hasActions) {
      message.warning('Please add actions (movements/passes) before adding a new frame.');
      return;
    }

    // Deep copy current entities to start the new frame
    const nextEntitiesMap: Record<ViewMode, BoardEntity[]> = JSON.parse(JSON.stringify(entitiesMap));
    
    // Helper to update entities based on actions
    const updateEntitiesForMode = (mode: ViewMode) => {
      const actions = actionsMap[mode];
      const entities = nextEntitiesMap[mode];
      
      // 1. Move Players based on their movement paths
      entities.forEach(entity => {
        if (entity.type === 'player') {
          const action = actions.find(a => a.playerId === entity.id && ['move', 'dribble', 'screen', 'steal', 'block'].includes(a.type));
          if (action && action.path.length > 0) {
            const endPoint = action.path[action.path.length - 1];
            entity.position = { ...endPoint };
            
            // Update rotation to face movement direction
            if (action.path.length > 1) {
               const prev = action.path[action.path.length - 2];
               const angle = Math.atan2(endPoint.y - prev.y, endPoint.x - prev.x) * 180 / Math.PI;
               (entity as PlayerType).rotation = angle + 90; 
            }
          }
        }
      });

      // 2. Move Ball based on Pass/Shoot or Owner Movement
      const ball = entities.find(e => e.type === 'ball') as BallType | undefined;
      if (ball && ball.ownerId) {
          // Check if owner passed or shot
          const ownerAction = actions.find(a => a.playerId === ball.ownerId && ['pass', 'shoot'].includes(a.type));
          
          if (ownerAction && ownerAction.path.length > 0) {
            // Ball travels to end of path
            const endPoint = ownerAction.path[ownerAction.path.length - 1];
            ball.position = { ...endPoint };
            ball.ownerId = undefined; // Ball is released
            
            // Check if it landed on another player (Pass reception)
            const receiver = entities.find(e => e.type === 'player' && e.id !== ownerAction.playerId && 
              Math.hypot(e.position.x - endPoint.x, e.position.y - endPoint.y) < 30 // Threshold
            );
            if (receiver) {
              ball.ownerId = receiver.id;
            }
          } else {
            // Ball stays with owner (who might have moved in step 1)
            const owner = entities.find(e => e.id === ball.ownerId);
            if (owner) {
              ball.position = { ...owner.position };
            }
          }
      }
    };

    updateEntitiesForMode('full');
    updateEntitiesForMode('half');

    const newFrame: Frame = {
      id: uuidv4(),
      entitiesMap: nextEntitiesMap,
      actionsMap: { full: [], half: [] } // Clear actions for new frame
    };
    
    const newFrames = [...frames];
    // Insert after current frame
    newFrames.splice(currentFrameIndex + 1, 0, newFrame);
    
    isSwitchingFrame.current = true; // Prevent useEffect from overwriting new frame with old state
    setFrames(newFrames);
    setCurrentFrameIndex(currentFrameIndex + 1);
    
    // Explicitly update state to match the new frame
    // This prevents "disappearing" entities if the useEffect hasn't fired yet
    setEntitiesMap(newFrame.entitiesMap);
    setActionsMap(newFrame.actionsMap);
  };

  const handleSelectFrame = (index: number) => {
    isSwitchingFrame.current = true; // Prevent useEffect from overwriting target frame with old state
    setCurrentFrameIndex(index);
    setEntitiesMap(frames[index].entitiesMap);
    setActionsMap(frames[index].actionsMap);
    setIsPlaying(false);
    setAnimationProgress(0);
  };



  // Delete Frame
  const handleDeleteFrame = () => {
    if (frames.length <= 1) return; // Don't delete the last frame
    
    const newFrames = frames.filter((_, idx) => idx !== currentFrameIndex);
    
    // Adjust current index
    let newIndex = currentFrameIndex;
    if (newIndex >= newFrames.length) {
      newIndex = newFrames.length - 1;
    }
    
    isSwitchingFrame.current = true;
    setFrames(newFrames);
    setCurrentFrameIndex(newIndex);
    setEntitiesMap(newFrames[newIndex].entitiesMap);
    setActionsMap(newFrames[newIndex].actionsMap);
  };

  const handleSnapshot = () => {
    if (stageRef.current) {
      const uri = stageRef.current.toDataURL();
      const link = document.createElement('a');
      link.download = 'tactics-board.png';
      link.href = uri;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleExport = () => {
    if (frames.length <= 1 && actionsMap.full.length === 0 && actionsMap.half.length === 0) {
      message.warning('Nothing to export!');
      return;
    }

    // Reset to start
    setIsPlaying(false);
    setCurrentFrameIndex(0);
    setAnimationProgress(0);
    setEntitiesMap(frames[0].entitiesMap);
    setActionsMap(frames[0].actionsMap);
    
    // Clear selection for clean recording
    setSelectedId(null);
    setSelectedActionId(null);
    setActiveTool(null);

    message.loading('Preparing recording...', 1);

    // Give it a moment to render frame 0 then start
    setTimeout(() => {
      const layer = stageRef.current.findOne('Layer');
      const canvas = layer.getCanvas()._canvas;
      
      // Check if stream capture is supported
      if (!canvas.captureStream) {
        message.error('Browser does not support canvas recording.');
        return;
      }

      const stream = canvas.captureStream(30); // 30 FPS
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      
      recorder.ondataavailable = (e: any) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      
      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'tactics-animation.webm';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        recordedChunksRef.current = [];
        message.success('Export complete! (Saved as WebM video)');
      };
      
      mediaRecorderRef.current = recorder;
      recorder.start();
      
      // Start Animation
      setIsPlaying(true);
      setIsAnimationMode(true);
    }, 500);
  };

  const handleStop = () => {
    setIsPlaying(false);
    setIsAnimationMode(false); // Exit animation mode
    isSwitchingFrame.current = true;
    setCurrentFrameIndex(0);
    setEntitiesMap(frames[0].entitiesMap);
    setActionsMap(frames[0].actionsMap);
    setAnimationProgress(0);
  };

  const handleUndo = () => {
    // Reset Animation: Delete all frames except the first one
    // AND clear actions in the first frame to start fresh
    const firstFrame = frames[0];
    
    // Create a clean first frame with NO actions
    const cleanFirstFrame = {
        ...firstFrame,
        actionsMap: { full: [], half: [] }
    };

    isSwitchingFrame.current = true;
    setFrames([cleanFirstFrame]);
    setCurrentFrameIndex(0);
    setEntitiesMap(cleanFirstFrame.entitiesMap);
    setActionsMap(cleanFirstFrame.actionsMap);
    setIsPlaying(false);
    setAnimationProgress(0);
    message.success('Animation reset. All frames deleted.');
  };

  // Top Bar Render
  const renderTopBar = () => (
    <div style={{
      height: '58px',
      background: '#2A2A2A', // Dark Grey
      display: 'flex',
      alignItems: 'center',
      padding: '0 20px',
      justifyContent: 'space-between',
      borderTop: '2px solid #3A3A3A', // Lighter line
      width: stageWidth + 4, // Match board width (stage + borders)
      boxSizing: 'border-box',
      zIndex: 100,
      marginTop: '0px', // Removed margin
      borderRadius: '0 0 4px 4px' // Rounded bottom corners only
    }}>
      <style>
        {`
          .toolbar-icon-btn {
            opacity: 0.7;
            transition: all 0.2s ease-in-out;
          }
          .toolbar-icon-btn:hover {
            opacity: 1 !important;
            transform: scale(1.15);
            color: #3A7AFE !important; /* Accent Blue */
          }
        `}
      </style>

      {/* Left Tools */}
      <div style={{ display: 'flex', gap: '15px' }}>
        <Tooltip title="Snapshot"><Button type="text" className="toolbar-icon-btn" icon={<CameraOutlined />} style={{ color: '#E5E5E5', fontSize: '20px' }} onClick={handleSnapshot} /></Tooltip>
        <Tooltip title="Export Animation (Video)"><Button type="text" className="toolbar-icon-btn" icon={<DownloadOutlined />} style={{ color: '#E5E5E5', fontSize: '20px' }} onClick={handleExport} /></Tooltip>
        <Tooltip title="Export Sketch Data (JSON)"><Button type="text" className="toolbar-icon-btn" icon={<FileTextOutlined />} style={{ color: '#E5E5E5', fontSize: '20px' }} onClick={handleExportJSON} /></Tooltip>
      </div>

      {/* Center Frames */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.05)', padding: '7px 12px', borderRadius: '6px' }}>
        {frames.map((frame, idx) => (
          <Tooltip key={frame.id} title={`Frame ${idx + 1}`}>
            <Button 
              type={currentFrameIndex === idx ? 'primary' : 'default'}
              onClick={() => handleSelectFrame(idx)}
              style={{ 
                background: currentFrameIndex === idx ? '#3A7AFE' : 'transparent', // Accent Blue
                color: currentFrameIndex === idx ? '#fff' : 'rgba(255,255,255,0.7)',
                border: currentFrameIndex === idx ? 'none' : '1px solid rgba(255,255,255,0.2)',
                minWidth: '36px',
                fontSize: '15px',
                fontWeight: currentFrameIndex === idx ? 800 : 600
              }}
            >
              {idx + 1}
            </Button>
          </Tooltip>
        ))}
        <Tooltip title="Add Frame">
          <Button 
            icon={<PlusOutlined />} 
            onClick={handleAddFrame}
            className="toolbar-icon-btn"
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)' }}
          />
        </Tooltip>
        <Tooltip title="Delete Frame">
          <Button 
            icon={<DeleteOutlined />} 
            onClick={handleDeleteFrame}
            disabled={frames.length <= 1}
            className="toolbar-icon-btn"
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', marginLeft: '5px' }}
          />
        </Tooltip>
        <Tooltip title="Reset Animation (Delete All Frames)">
          <Button 
            icon={<UndoOutlined />} 
            onClick={handleUndo}
            className="toolbar-icon-btn"
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', marginLeft: '5px' }}
          />
        </Tooltip>
        
        {/* Frame Description Input */}
        <div style={{ marginLeft: '10px', borderLeft: '1px solid rgba(255,255,255,0.2)', paddingLeft: '10px' }}>
            <Input 
                className="styled-dark-input styled-dark-input-pill"
                placeholder="Frame Description (e.g. Pick & Roll)" 
                value={frames[currentFrameIndex]?.description || ''}
                onChange={(e) => {
                    const newDesc = e.target.value;
                    setFrames(prev => {
                        const newFrames = [...prev];
                        newFrames[currentFrameIndex] = {
                            ...newFrames[currentFrameIndex],
                            description: newDesc
                        };
                        return newFrames;
                    });
                }}
                prefix={<EditOutlined style={{ color: '#FFFFFF', marginRight: '4px' }} />}
                style={{ width: '220px' }}
            />
        </div>
      </div>

      {/* Right Playback */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        <Dropdown overlay={
          <Menu onClick={({ key }) => setPlaybackSpeed(parseFloat(key))}>
            <Menu.Item key="0.25">0.25x</Menu.Item>
            <Menu.Item key="0.5">0.5x</Menu.Item>
            <Menu.Item key="1.0">1.0x</Menu.Item>
            <Menu.Item key="1.5">1.5x</Menu.Item>
            <Menu.Item key="2.0">2.0x</Menu.Item>
          </Menu>
        } placement="topCenter">
          <Button 
            type="text" 
            style={{ color: 'rgba(255,255,255,0.7)', fontSize: '16px', fontWeight: 800, minWidth: '60px' }}
          >
            {playbackSpeed}x
          </Button>
        </Dropdown>

        <Tooltip title="Stop">
          <Button 
            type="text" 
            icon={<div style={{ width: '12px', height: '12px', backgroundColor: 'currentColor', borderRadius: '1px' }} />} 
            onClick={handleStop}
            style={{ color: 'rgba(255,255,255,0.7)', fontSize: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} 
          />
        </Tooltip>
        <Tooltip title="Pause">
          <Button 
            type="text" 
            icon={<PauseOutlined />} 
            onClick={() => setIsPlaying(false)}
            style={{ color: isPlaying ? '#fff' : 'rgba(255,255,255,0.3)', fontSize: '24px' }} 
          />
        </Tooltip>
        <Tooltip title="Play">
          <Button 
            type="text" 
            icon={<CaretRightOutlined />} 
            onClick={() => {
              // Auto-restart if at the end OR if starting fresh from edit mode
              if (!isAnimationMode || currentFrameIndex >= frames.length - 1) {
                isSwitchingFrame.current = true;
                setCurrentFrameIndex(0);
                setEntitiesMap(frames[0].entitiesMap);
                setActionsMap(frames[0].actionsMap);
                setAnimationProgress(0);
              }
              
              setIsAnimationMode(true);
              setIsPlaying(true);
            }}
            style={{ color: isPlaying ? 'rgba(255,255,255,0.3)' : '#fff', fontSize: '24px' }} 
          />
        </Tooltip>
      </div>
    </div>
  );

  

  // Auto-Analyze when frames change (Debounced) - DISABLED
  // React.useEffect(() => {
  //     if (frames.length >= 2) {
  //         const timer = setTimeout(() => {
  //         }, 1000);
  //         return () => clearTimeout(timer);
  //     }
  // }, [frames, viewMode]);


  const handleExportJSON = () => {    const data = {
      meta: {
        version: "1.0",
        timestamp: new Date().toISOString(),
        viewMode: viewMode,
        frameCount: frames.length,
        name: "Exported Tactic",
        description: ""
      },
      frames: frames.map((frame, index) => {
        // Use current state for the current frame to ensure latest changes are captured
        const isCurrent = index === currentFrameIndex;
        const entities = isCurrent ? entitiesMap[viewMode] : frame.entitiesMap[viewMode];
        const actions = isCurrent ? actionsMap[viewMode] : frame.actionsMap[viewMode];

        return {
          id: frame.id,
          index: index,
          description: frame.description || "", // Export description
          entities: entities.map(e => {
            if (e.type === 'player') {
              const p = e as PlayerType;
              return {
                id: p.id,
                type: 'player',
                team: p.team,
                number: p.number,
                role: p.role,
                position: p.position,
                rotation: p.rotation
              };
            } else {
              const b = e as BallType;
              return {
                id: b.id,
                type: 'ball',
                position: b.position,
                ownerId: b.ownerId
              };
            }
          }),
          actions: actions.map(a => ({
            id: a.id,
            type: a.type,
            playerId: a.playerId,
            path: a.path,
            color: a.color
          }))
        };
      })
    };

    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tactics_sketch_${new Date().getTime()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success('Sketch data exported to JSON!');
  };

  /**
   * Retroactively assign actionTag to any action that was saved without one,
   * then apply frame-level refinement (PnR_BH upgrade when screen present).
   * hasBall heuristic: shoot/dribble/pass = true, move/screen/steal/block = false.
   */
  const retagActions = (actions: Action[]): Action[] => {
    // Step 1: per-action tagging
    const tagged = actions.map(a => {
      if (a.actionTag) return a; // already tagged — keep as-is
      const start = a.path[0];
      const end   = a.path[a.path.length - 1];
      if (!start || !end) return a;
      const hasBall = (a.type === 'shoot' || a.type === 'dribble' || a.type === 'pass');
      const tag = deriveActionTag(a.type, hasBall, start.x, start.y, end.x, end.y);
      return tag ? { ...a, actionTag: tag } : a;
    });

    // Step 2: frame-level refinement — upgrade Isolation → PnR_BH when screen present
    const hasBallMap = new Map<string, boolean>();
    tagged.forEach(a => {
      const isBallAction = (a.type === 'shoot' || a.type === 'dribble' || a.type === 'pass');
      if (isBallAction) hasBallMap.set(a.playerId, true);
    });
    return refineFrameActionTags(tagged, hasBallMap);
  };

  const handleLoadTactic = async (tacticId: string, loadMode: 'play' | 'edit' = 'play') => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/tactics/${tacticId}`);
      if (!response.ok) throw new Error('Failed to fetch tactic details');
      const responseData = await response.json();
      
      // Unwrap animation_data if present (New Schema Support)
      const data = responseData.animation_data || responseData;

      // Store metadata for editing
      setCurrentTacticId(tacticId);
      setCurrentTacticMetadata({
          name: responseData.name,
          description: responseData.description,
          category: responseData.category,
          sub_category: responseData.sub_category,
          tags: responseData.tags,
          external_links: responseData.external_links,
          preview_image: responseData.preview_image
      });

      // Check if it's the new Export format (Sketch Data)
      if (data.frames && Array.isArray(data.frames)) {
          // If meta is missing, mock it
          const meta = data.meta || { viewMode: 'full' };
          const loadedViewMode = meta.viewMode || 'full';
          
          const newFrames: Frame[] = data.frames.map((f: any) => {
              // Reconstruct entities and actions for the specific viewMode
              const entities = f.entities || [];
              const actions = retagActions(f.actions || []);
              
              // We populate both viewModes with the same data to prevent crashes, 
              // but ideally we should respect the viewMode it was created in.
              return {
                  id: f.id || uuidv4(),
                  entitiesMap: {
                      full: entities,
                      half: entities // Duplicate for safety
                  },
                  actionsMap: {
                      full: actions,
                      half: actions // Duplicate for safety
                  },
                  description: f.description || "" // Support description if added later
              };
          });

          setFrames(newFrames);
          setEntitiesMap(newFrames[0].entitiesMap);
          setActionsMap(newFrames[0].actionsMap);
          setCurrentFrameIndex(0);
          
          // Switch view mode if needed
          if (loadedViewMode !== viewMode) {
              setViewMode(loadedViewMode as ViewMode);
              message.info(`Switched to ${loadedViewMode} court view`);
          }

          // Only auto-play if mode is 'play'
          if (loadMode === 'play') {
              setIsPlaying(true);
              setIsAnimationMode(true);
          } else {
              // Edit mode: don't auto-play
              setIsPlaying(false);
              setIsAnimationMode(false);
          }
          message.success(`Loaded tactic: ${data.meta?.name || responseData.name || 'Custom Tactic'}`);
          return;
      }

      // Fallback: Old Format (1-3-1 Offense style)
      // Parse tactic data into frames
      const newFrames: Frame[] = [];
      
      // Initial Setup (Frame 0)
      // Hardcoded 1-3-1 positions for now as they are not in the JSON
      // In a real app, these should come from the JSON
      const initialEntities: BoardEntity[] = [
        { id: '1', type: 'player', team: 'red', number: '1', role: 'PG', position: { x: 380, y: 120 }, rotation: 0 },
        { id: '2', type: 'player', team: 'red', number: '2', role: 'SG', position: { x: 100, y: 300 }, rotation: 0 },
        { id: '3', type: 'player', team: 'red', number: '3', role: 'SF', position: { x: 660, y: 300 }, rotation: 0 },
        { id: '4', type: 'player', team: 'red', number: '4', role: 'PF', position: { x: 250, y: 350 }, rotation: 0 },
        { id: '5', type: 'player', team: 'red', number: '5', role: 'C', position: { x: 380, y: 250 }, rotation: 0 },
        { id: 'ball', type: 'ball', position: { x: 380, y: 130 }, ownerId: '1' }
      ];

      newFrames.push({
        id: '0',
        entitiesMap: { full: initialEntities, half: initialEntities },
        actionsMap: { full: [], half: [] },
        description: "Initial Setup"
      });

      // Process steps
      if (data.steps && Array.isArray(data.steps)) {
        data.steps.forEach((stepText: string, index: number) => {
          const stepIndex = index + 1; // 1-based index for matching overlays
          
          // Clone previous frame entities
          const prevEntities = [...newFrames[newFrames.length - 1].entitiesMap.full];
          const currentEntities = prevEntities.map(e => ({ ...e })); 
          
          // Find overlays for this step
          const overlay = data.overlays?.find((o: any) => o.for_step === stepIndex);
          const actions: Action[] = [];

          if (overlay && overlay.arrows) {
            overlay.arrows.forEach((arrow: any) => {
              // Create action from arrow
              const actionId = uuidv4();
              let path: Position[] = [];
              
              // Find start position from entity
              const fromEntity = currentEntities.find(e => e.id === String(arrow.fromId));
              const startPos = fromEntity ? fromEntity.position : { x: 0, y: 0 };
              
              // Determine end position
              let endPos = { x: 0, y: 0 };
              if (arrow.endPoint) {
                endPos = arrow.endPoint;
              } else if (arrow.toId) {
                const toEntity = currentEntities.find(e => e.id === String(arrow.toId));
                endPos = toEntity ? toEntity.position : { x: 0, y: 0 };
              }

              // Construct path
              path = [startPos];
              if (arrow.controlPoint) {
                path.push(arrow.controlPoint);
              }
              path.push(endPos);

              actions.push({
                id: actionId,
                type: arrow.type === 'pass' ? 'pass' : arrow.type === 'screen' ? 'screen' : 'move',
                playerId: String(arrow.fromId),
                path: path,
                color: arrow.type === 'pass' ? '#ff9c6e' : arrow.type === 'screen' ? '#8c8c8c' : '#1890ff'
              });

              // Update entity position if it's a move
              if (arrow.type === 'move' || arrow.type === 'curve_move') {
                 const entityIndex = currentEntities.findIndex(e => e.id === String(arrow.fromId));
                 if (entityIndex !== -1) {
                   currentEntities[entityIndex] = {
                     ...currentEntities[entityIndex],
                     position: endPos
                   };
                 }
              }
              // Update ball position if it's a pass
              if (arrow.type === 'pass') {
                 const ballIndex = currentEntities.findIndex(e => e.type === 'ball');
                 if (ballIndex !== -1) {
                    const ball = currentEntities[ballIndex] as BallType;
                    currentEntities[ballIndex] = {
                      ...ball,
                      position: endPos,
                      ownerId: String(arrow.toId)
                    };
                 }
              }
            });
          }

          newFrames.push({
            id: String(stepIndex),
            entitiesMap: { full: currentEntities, half: currentEntities },
            actionsMap: { full: actions, half: actions },
            description: stepText
          });
        });
      }

      setFrames(newFrames);
      setEntitiesMap(newFrames[0].entitiesMap);
      setActionsMap(newFrames[0].actionsMap);
      setCurrentFrameIndex(0);
      setIsPlaying(true);
      setIsAnimationMode(true);
      message.success(`Loaded tactic: ${data.tactic}`);

    } catch (error) {
      console.error('Error loading tactic:', error);
      message.error('Failed to load tactic');
    }
  };

  const handleOpenSaveModal = () => {
    // Capture screenshot
    if (stageRef.current) {
        try {
            const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2 });
            setSavePreviewImage(dataUrl);
        } catch (e) {
            console.error("Screenshot failed", e);
        }
    }
    
    // Pre-fill form if editing
    if (currentTacticId && currentTacticMetadata) {
        saveForm.setFieldsValue({
            name: currentTacticMetadata.name,
            description: currentTacticMetadata.description,
            category: currentTacticMetadata.category,
            sub_category: currentTacticMetadata.sub_category,
            tags: currentTacticMetadata.tags,
            external_links: currentTacticMetadata.external_links,
            preview_source: currentTacticMetadata.preview_image ? 'custom' : 'auto',
            custom_image_url: currentTacticMetadata.preview_image
        });
    } else {
        saveForm.resetFields();
    }

    setIsSaveModalVisible(true);
  };

  const handleSaveTactic = async (values: any) => {
    setSaveLoading(true);
    try {
        // Determine preview image source
        let finalPreviewImage = savePreviewImage;
        if ((values.preview_source === 'custom' || values.preview_source === 'upload') && values.custom_image_url) {
            finalPreviewImage = values.custom_image_url;
        }

        const selectedTags: string[] = Array.isArray(values.tags) ? values.tags : [];
        const playtypes = selectedTags.map((code) => ({
          code,
          name: ATOMIC_ACTIONS[code]?.name || code,
          description: ATOMIC_ACTIONS[code]?.description || '',
        }));

        const tacticData = {
            id: currentTacticId || uuidv4(),
            ...values,
          tags: selectedTags,
          playtypes,
            preview_image: finalPreviewImage,
            animation_data: {
                meta: {
                  version: "2.0",
                  timestamp: new Date().toISOString(),
                  viewMode: viewMode,
                  frameCount: frames.length,
                  name: values.name,
                  description: values.description
                },
                frames: frames.map(f => ({
                    id: f.id,
                    description: f.description,
                    entities: f.entitiesMap[viewMode], 
                    actions: f.actionsMap[viewMode],
                }))
            }
        };

        const response = await fetch(API_ENDPOINTS.TACTICS, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(tacticData)
        });

        if (!response.ok) throw new Error("Save failed");
        
        // Update state to reflect saved tactic (becomes currently edited)
        setCurrentTacticId(tacticData.id);
        setCurrentTacticMetadata({
            ...values,
          tags: selectedTags,
          playtypes,
            preview_image: finalPreviewImage
        });
        
        message.success("Tactic saved to Gallery!");
        setIsSaveModalVisible(false);
        // saveForm.resetFields(); // Don't reset, so we keep values if we open again
    } catch (e) {
        console.error(e);
        message.error("Failed to save tactic");
    } finally {
        setSaveLoading(false);
    }
  };

  /* --- Legacy NBA Player Search Modal render fn (commented out) ---
  const playerSearchModal = () => { ... };
  --- end NBA --- */

  // Context Menu Handler for Player — right-click opens tag assignment
  const handlePlayerContextMenu = (e: any, id: string) => {
    e.evt.preventDefault();
    setTargetPlayerId(id);
    setIsTagModalVisible(true);
  };

  /* --- Legacy NBA context menu (commented out) ---
  const handlePlayerContextMenuNBA = (e: any, id: string) => {
    e.evt.preventDefault();
    setTargetPlayerId(id);
    Modal.confirm({
      title: 'Assign Real NBA Player',
      content: 'Do you want to assign a real NBA player profile to this entity?',
      onOk: () => {
        setIsPlayerSearchModalVisible(true);
        setSearchQuery('');
        setSearchResults([]);
      }
    });
  };
  --- end NBA --- */

  // Number → position mapping for auto-role derivation
  const NUMBER_TO_ROLE: Record<string, string> = { '1': 'PG', '2': 'SG', '3': 'SF', '4': 'PF', '5': 'C' };

  // Assign a lineup tag to the right-clicked player (role is auto-derived from jersey number)
  const handleAssignTag = (tag: OffensiveRoleCode) => {
    if (!targetPlayerId) return;
    const updateEntities = (list: BoardEntity[]) =>
      list.map(e => {
        if (e.id === targetPlayerId && e.type === 'player') {
          const p = e as PlayerType;
          const autoRole = NUMBER_TO_ROLE[p.number] ?? p.role;
          return { ...p, playerTag: tag, role: autoRole };
        }
        return e;
      });

    setEntitiesMap(prev => ({
      ...prev,
      [viewMode]: updateEntities(prev[viewMode])
    }));
    setFrames(prevFrames =>
      prevFrames.map(frame => ({
        ...frame,
        entitiesMap: {
          ...frame.entitiesMap,
          [viewMode]: updateEntities(frame.entitiesMap[viewMode])
        }
      }))
    );
    setIsTagModalVisible(false);
    message.success(`Tag "${tag}" assigned!`);
  };

  // Legacy NBA search/assign handlers removed (handleSearchPlayers, handleAssignPlayer)

  // ---------------------------------------------------------------------------
  // AI Chat Tool Call Handlers
  // ---------------------------------------------------------------------------

  /** Role lookup for jersey numbers 1-5 */
  const AI_PLAYER_ROLES: Record<string, string> = {
    '1': 'PG', '2': 'SG', '3': 'SF', '4': 'PF', '5': 'C',
  };

  /** Move a player by jersey number. normX/normY are 0-100 (normalised court).
   *  If the player doesn't exist on the board yet, it is created automatically. */
  const handleAIMovePlayer = (playerNumber: string, normX: number, normY: number) => {
    const canvasX = (normX / 100) * COURT_WIDTH;
    const canvasY = (normY / 100) * COURT_HEIGHT;
    const numStr = String(playerNumber);

    setEntitiesMap(prev => {
      const updated = { ...prev };
      const list = [...(prev[viewMode] || [])];
      const idx = list.findIndex(e => e.type === 'player' && (e as any).number === numStr);

      if (idx !== -1) {
        // Player exists �?just update position
        list[idx] = { ...list[idx], position: { x: canvasX, y: canvasY } };
      } else {
        // Player not on board �?create it so AI tactics work on an empty board
        const newPlayer: import('../../types').Player = {
          id: `ai-player-${numStr}-${Date.now()}`,
          type: 'player',
          number: numStr,
          team: 'blue',
          position: { x: canvasX, y: canvasY },
          role: AI_PLAYER_ROLES[numStr] ?? 'PG',
        };
        list.push(newPlayer);
      }

      updated[viewMode] = list;
      return updated;
    });
  };

  /** Transfer ball ownership between players by jersey number. */
  const handleAIPassBall = (fromNumber: string, toNumber: string) => {
    setEntitiesMap(prev => {
      const updated = { ...prev };
      const list = [...(prev[viewMode] || [])];
      const receiver = list.find(e => e.type === 'player' && (e as any).number === String(toNumber));
      const ballIdx = list.findIndex(e => e.type === 'ball');
      if (receiver && ballIdx !== -1) {
        list[ballIdx] = {
          ...list[ballIdx],
          position: { ...receiver.position },
          ownerId: receiver.id,
        } as any;
      }
      updated[viewMode] = list;
      return updated;
    });
  };

  /** Move screener to the screen position (normalised 0-100). */
  const handleAIDrawScreen = (screenerNumber: string, normX: number, normY: number) => {
    handleAIMovePlayer(screenerNumber, normX, normY);
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'row', 
      background: APP_BACKGROUND,
      minHeight: '100vh',
      width: '100%',
      overflow: 'hidden',
      position: 'relative',
    }}>
      
      {/* Left Sidebar */}
      {/* Left Sidebar (The Ultimate Zoning) - Aggressively Memoized */}
      {React.useMemo(() => (
        <div style={{
          width: '75px',
          background: 'rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '16px',
          paddingBottom: '16px',
          borderRight: '1px solid rgba(255,255,255,0.1)',
          zIndex: 20
        }}>
          {/* 1. VIEW */}
          <SidebarSectionLabel text="VIEW" />
          <SidebarButton icon={<ExpandOutlined />} active={viewMode === 'full'} onClick={() => setViewMode('full')} tooltip="Full Court" />
          <SidebarButton icon={<CompressOutlined />} active={viewMode === 'half'} onClick={() => setViewMode('half')} tooltip="Half Court" />
          <SidebarButton icon={<FullscreenOutlined />} onClick={() => document.documentElement.requestFullscreen()} tooltip="Fullscreen" />
          
          <SidebarDivider />
          
          {/* 2. OPPONENT */}
          <SidebarSectionLabel text="OPPONENT" />
          <SidebarButton icon={showGhostDefense ? <EyeOutlined /> : <EyeInvisibleOutlined />} active={showGhostDefense} onClick={() => setShowGhostDefense(!showGhostDefense)} tooltip="Toggle Ghost Defense" />
          
          <SidebarDivider />
          
          {/* 3. GALLERY */}
          <SidebarSectionLabel text="GALLERY" />
          <SidebarButton icon={<BookOutlined />} active={isTacticsLibraryVisible} onClick={() => setIsTacticsLibraryVisible(true)} tooltip="Tactics Gallery" />
          <SidebarButton icon={<FolderAddOutlined />} onClick={handleOpenSaveModal} tooltip="Save Tactic" />
          
          <SidebarDivider />
          
          {/* 4. ANALYTICS */}
          <SidebarSectionLabel text="ANALYTICS" />
          <SidebarButton icon={<RadarChartOutlined />} active={isDiagnosticOpen} onClick={() => setIsDiagnosticOpen(prev => !prev)} tooltip="AI Diagnostics" />

          {/* Spacer to push SYSTEM to the bottom */}
          <div style={{ flex: 1 }} />
          
          {/* 5. SYSTEM / ACTIONS */}
          <SidebarDivider />
          <SidebarSectionLabel text="SYSTEM" />
          <SidebarButton 
            icon={<DeleteOutlined style={{ color: 'rgba(255, 100, 100, 0.7)' }} />} 
            onClick={clearBoard} 
            tooltip="Clear Board" 
          />
        </div>
      ), [viewMode, showGhostDefense, isTacticsLibraryVisible, isDiagnosticOpen])}

      {/* Main Content Area */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        position: 'relative',
        padding: '20px',
        overflow: 'auto'
      }}>
        
        <div style={{ display: 'flex', flexDirection: 'row', gap: '20px', alignItems: 'flex-start' }}>
          
          {/* Left Column: Board + Player Panel */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Main Board Area */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div 
                ref={boardRef}
                onDrop={handleDrop} 
                onDragOver={handleDragOver}
                style={{ ...boardStyle, position: 'relative', borderRadius: '8px 8px 0 0', borderBottom: 'none' }}
              >
                {renderPlayerMenu()}
                {renderActionMenu()}

                <Stage 
                  width={stageWidth} 
                  height={stageHeight} 
                  ref={stageRef}
                  onClick={handleStageClick}
                  onTap={handleStageClick}
                  onMouseDown={handleStageMouseDown}
                  onMouseMove={handleStageMouseMove}
                  onMouseUp={handleStageMouseUp}
                >
                  <Layer
                    rotation={isVertical ? 90 : 0}
                    x={isVertical ? stageWidth : 0}
                    y={0}
                  >{memoizedCourt}{showGhostDefense && (
                      <GhostDefenseLayer 
                        entities={entities} 
                        viewMode={viewMode} 
                        currentFrameIndex={currentFrameIndex}
                      />
                    )}
                    <DanmakuLayer 
                      actions={actions}
                      entities={entities}
                      viewMode={viewMode}
                      isPlaying={isAnimationMode} // Or isPlaying state? isAnimationMode seems to be the mode toggle. isPlaying is the playback state.
                      animationProgress={animationProgress}
                    />
                    <ActionLayer 
                      actions={actions}
                      currentAction={currentAction}
                      selectedActionId={selectedActionId}
                      onSelectAction={handleActionSelect}
                      onActionPointChange={handleActionPointChange}
                      viewMode={viewMode}
                    />
                    
                    {/* --- Z-Index Fix: Render Layers in Order --- */}

                    {/* 1. Render Players First (Bottom Layer) */}
                    {/* 1. Render all players (bottom layer) */}
                  {entities.filter(e => e.type === 'player').map(entity => {
                      const hasBall = entities.some(e => e.type === 'ball' && (e as BallType).ownerId === entity.id);
                      return (
                        <Player 
                          key={entity.id} 
                          player={entity as PlayerType} 
                          onDragMove={handleEntityDrag}
                          rotationOffset={isVertical ? -90 : 0}
                          isSelected={selectedId === entity.id}
                          hasBall={hasBall}
                          draggable={!activeTool} 
                          onSelect={() => {
                            setSelectedId(entity.id);
                            setSelectedActionId(null); 
                          }}
                          onRotate={handleEntityRotate}
                          stageWidth={stageWidth}
                          stageHeight={stageHeight}
                          viewMode={viewMode}
                          scale={(entity as any).scale || 1}
                          armExtension={(entity as any).armExtension || 0}
                          actionType={(entity as any).actionType}
                          isWarning={fitErrors.some(err => err.playerId === entity.id)}
                        />
                      );
                  })}

                  {/* 2. Render all balls last (top layer - ensures ball always floats above players) */}
                  {entities.filter(e => e.type === 'ball').map(entity => (
                      <Ball 
                        key={entity.id} 
                        ball={entity as BallType} 
                        onDragMove={handleEntityDrag}
                        draggable={!activeTool}
                        stageWidth={stageWidth}
                        stageHeight={stageHeight}
                        isSelected={selectedId === entity.id}
                        onSelect={() => {
                          setSelectedId(entity.id);
                          setSelectedActionId(null);
                        }}
                        viewMode={viewMode}
                      />
                  ))}
                  </Layer>
                </Stage>

                {/* Tactic Step Description Overlay */}
                {isAnimationMode && frames[currentFrameIndex]?.description && (
                  <div style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '20px',
                    maxWidth: '80%',
                    textAlign: 'center',
                    zIndex: 10,
                    pointerEvents: 'none',
                    fontSize: '20px',
                    fontWeight: 800
                  }}>
                    {frames[currentFrameIndex].description}
                  </div>
                )}

                {/* Roster-Fit Error Overlay (Phase 4 – Micro-View) - Memoized */}
                {React.useMemo(() => (
                  <RosterFitPanel
                    errors={fitErrors}
                    players={entities.filter(e => e.type === 'player') as PlayerType[]}
                  />
                ), [fitErrors, entities])}
              </div>
              {React.useMemo(() => renderTopBar(), [frames, currentFrameIndex, isPlaying, playbackSpeed, viewMode, isAnimationMode])}
            </div>

            {/* Expandable Team Roster Panel (Below Board) - Memoized */}
            {React.useMemo(() => (
              <div style={{ 
                width: stageWidth,
                background: 'rgba(20, 25, 35, 0.75)',
                backdropFilter: 'blur(16px)',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                height: isRosterCollapsed ? '38px' : '136px',
                overflow: 'hidden'
              }}>
                {/* Drawer Handle / Header */}
                <div 
                  onClick={() => setIsRosterCollapsed(!isRosterCollapsed)}
                  style={{
                    height: '38px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    borderBottom: isRosterCollapsed ? 'none' : '1px solid rgba(255,255,255,0.06)',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    fontWeight: 800,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    background: 'rgba(255,255,255,0.02)'
                  }}
                >
                  Team Roster <span style={{ marginLeft: 6, fontSize: '12px', fontWeight: 800, color: '#FFFFFF' }}>{isRosterCollapsed ? '▲ FULL ROSTER' : '▼ HIDE'}</span>
                </div>
                
                {/* Content */}
                {!isRosterCollapsed && (
                  <div style={{ flex: 1, padding: '10px 16px', overflow: 'hidden' }}>
                      <PlayerInfoPanel 
                        players={entities.filter(e => e.type === 'player') as PlayerType[]} 
                        mode="bottom"
                        onTagClick={(id) => {
                          setTargetPlayerId(id);
                          setIsTagModalVisible(true);
                        }}
                      />
                  </div>
                )}
              </div>
            ), [entities, isRosterCollapsed, stageWidth])}
          </div>

          {/* Right Panel: AssetsBar */}
          <div style={{
              width: '112px',
              height: stageHeight,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
          }}>
            {!isAnimationMode && <AssetsBar onDragStart={() => {}} vertical={true} />}
          </div>
        </div>

        {/* Animation Controls (Absolute positioned at bottom) */}
        {isAnimationMode && (
        <div style={{ position: 'absolute', bottom: '20px', width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={{ 
              width: '80%', 
              background: 'rgba(0,0,0,0.6)', 
              padding: '10px 20px', 
              borderRadius: '8px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '15px',
              backdropFilter: 'blur(4px)'
            }}>
              <span style={{ color: '#E5E5E5', fontWeight: 'bold', minWidth: '80px' }}>
                Frame {currentFrameIndex + 1} / {frames.length}
              </span>
              
              <Slider 
                min={0} 
                max={frames.length - 1} 
                value={currentFrameIndex + animationProgress} 
                onChange={(val) => {
                   const frameIdx = Math.floor(val);
                   const progress = val - frameIdx;
                   
                   if (frameIdx !== currentFrameIndex) {
                     isSwitchingFrame.current = true;
                     setCurrentFrameIndex(frameIdx);
                     setEntitiesMap(frames[frameIdx].entitiesMap);
                     setActionsMap(frames[frameIdx].actionsMap);
                   }
                   
                   setAnimationProgress(progress);
                   setIsPlaying(false); // Pause when scrubbing
                }}
                step={0.01}
                style={{ flex: 1 }}
                trackStyle={{ backgroundColor: '#5C7ABD' }}
                handleStyle={{ borderColor: '#5C7ABD', backgroundColor: '#5C7ABD' }}
                railStyle={{ backgroundColor: '#3A3A3A' }}
              />
            </div>
        </div>
        )}

        
        {/* Player Tag Assignment Modal (right-click on player) */}
        {targetPlayerId && (() => {
          const currentPlayer = entitiesMap[viewMode].find(e => e.id === targetPlayerId) as PlayerType | undefined;
          return (
            <Modal
              title={<span style={{ color: '#fff', fontSize: '18px', fontWeight: 600 }}>Tag Player #{currentPlayer?.number || ''}</span>}
              open={isTagModalVisible}
              onCancel={() => setIsTagModalVisible(false)}
              footer={null}
              destroyOnClose
              className="dark-theme-modal"
              styles={{
                mask: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.6)' },
                content: { backgroundColor: '#1E232F', border: '1px solid rgba(250, 173, 20, 0.25)', boxShadow: '0 8px 32px rgba(0,0,0,0.8)', padding: '24px' },
                header: { backgroundColor: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '16px', marginBottom: '20px' },
                body: { color: '#a8b4cc' }
              }}
              closeIcon={<div style={{ color: '#8f9bb3', fontSize: '16px' }}>×</div>}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                <div style={{
                    width: 40, height: 40, borderRadius: '50%', 
                    background: currentPlayer?.team === 'red' ? '#e53e3e' : '#3182ce', 
                    color: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center', 
                    fontSize: '18px', fontWeight: 'bold', marginRight: 15,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.4)', border: '2px solid rgba(255,255,255,0.1)'
                }}>
                    {currentPlayer?.number}
                </div>
                <div>
                  <div style={{ color: '#fff', fontSize: 16, fontWeight: 500 }}>Select Role Identity</div>
                  <div style={{ color: '#8f9bb3', fontSize: 12 }}>Auto-assigned pos: <strong style={{ color: '#faad14' }}>{currentPlayer?.role || 'Unknown'}</strong></div>
                </div>
              </div>
              <Form
                layout="vertical"
                initialValues={{ tag: currentPlayer?.playerTag || undefined }}
                onFinish={(values) => handleAssignTag(values.tag as OffensiveRoleCode)}
              >
                <Form.Item 
                  label={<span style={{ color: '#a8b4cc', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>Role Tag</span>} 
                  name="tag" 
                  rules={[{ required: true, message: 'Please select a role tag' }]}
                >
                  <Select 
                    placeholder="Select a role..." 
                    size="large"
                    dropdownStyle={{ backgroundColor: '#252B3B', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    {OFFENSIVE_ROLE_TAG_OPTIONS.map(t => (
                      <Select.Option key={t.value} value={t.value}>
                        <strong style={{ color: '#faad14', marginRight: 8, display: 'inline-block', width: 40 }}>{t.value}</strong> 
                        <span style={{ color: '#d1d8e6' }}>{t.label.split(' - ')[0]}</span>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Button htmlType="submit" block style={{ 
                  marginTop: '12px', height: '46px', fontSize: '15px', fontWeight: 600, 
                  background: 'linear-gradient(135deg, #faad14 0%, #d48806 100%)', 
                  border: 'none', color: '#141414', borderRadius: '8px'
                }}>
                  Confirm Identity
                </Button>
              </Form>
            </Modal>
          );
        })()}
      </div>
      
      <TacticsLibrary 
        visible={isTacticsLibraryVisible}
        onClose={() => setIsTacticsLibraryVisible(false)}
        onSelectTactic={handleLoadTactic}
      />
      
      <ConfigProvider
        theme={{
          algorithm: theme.darkAlgorithm,
          token: {
            colorPrimary: '#F59E0B',
            colorInfo: '#3B82F6',
            colorBgBase: '#18181B',
            colorBgContainer: '#18181B',
            colorBgElevated: '#27272A',
            colorTextBase: '#F4F4F5',
            colorTextSecondary: '#A1A1AA',
            colorBorder: '#3F3F46',
            colorBorderSecondary: '#3F3F46',
            borderRadius: 6,
            fontFamily: 'Inter, Roboto, sans-serif'
          },
          components: {
            Modal: {
              contentBg: '#27272A',
              headerBg: '#27272A',
              titleColor: '#F4F4F5'
            }
          }
        }}
      >
        <Modal
          title={currentTacticId ? "Update Tactic" : "Save Tactic to Gallery"}
          open={isSaveModalVisible}
          onCancel={() => setIsSaveModalVisible(false)}
          confirmLoading={saveLoading}
          width={600}
          styles={{
            mask: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.6)' },
            header: { borderBottom: '1px solid #3F3F46', paddingBottom: '16px', marginBottom: '20px' }
          }}
          closeIcon={<div style={{ color: '#A1A1AA', fontSize: '16px' }}>×</div>}
          footer={[
            <Button key="cancel" onClick={() => setIsSaveModalVisible(false)} style={{ background: 'transparent', borderColor: '#3F3F46', color: '#F4F4F5' }}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" loading={saveLoading} onClick={() => saveForm.submit()} style={{ fontWeight: 600, color: '#18181B' }}>
              {currentTacticId ? "Update" : "Save"}
            </Button>,
          ]}
        >
          <Form
              form={saveForm}
              layout="vertical"
              onFinish={handleSaveTactic}
              initialValues={{ category: 'Offense', tags: [], sub_category: 'Actions', preview_source: 'auto' }}
          >
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.preview_source !== curr.preview_source || prev.custom_image_url !== curr.custom_image_url}>
                  {({ getFieldValue }) => {
                      const source = getFieldValue('preview_source');
                      const customUrl = getFieldValue('custom_image_url');
                      const displayImage = (source === 'custom' || source === 'upload') && customUrl ? customUrl : savePreviewImage;

                      return (
                          <div style={{ marginBottom: 16, textAlign: 'center', background: '#18181B', border: '1px solid #3F3F46', borderRadius: '8px', padding: 8 }}>
                              {displayImage ? (
                                  <img 
                                      src={displayImage} 
                                      alt="Preview" 
                                      style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }} 
                                      onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/400x200?text=Invalid+Image+URL')}
                                  />
                              ) : (
                                  <div style={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#A1A1AA' }}>
                                      No Preview Available
                                  </div>
                              )}
                              <div style={{ fontSize: 12, color: '#A1A1AA', marginTop: 4 }}>
                                  {source === 'auto' ? 'Auto-generated Preview' : 'Custom Preview'}
                              </div>
                          </div>
                      );
                  }}
              </Form.Item>

              <Form.Item name="preview_source" label="Preview Image Source">
                  <Radio.Group buttonStyle="solid">
                      <Radio.Button value="auto">Auto-Generated</Radio.Button>
                      <Radio.Button value="custom">URL</Radio.Button>
                      <Radio.Button value="upload">Upload</Radio.Button>
                  </Radio.Group>
              </Form.Item>

              <Form.Item 
                  noStyle 
                  shouldUpdate={(prev, curr) => prev.preview_source !== curr.preview_source || prev.custom_image_url !== curr.custom_image_url}
              >
                  {({ getFieldValue, setFieldsValue }) => {
                      const source = getFieldValue('preview_source');

                      if (source === 'custom') {
                          return (
                              <Form.Item name="custom_image_url" label="Image URL" rules={[{ required: true, message: 'Please enter image URL' }]}>
                                  <Input placeholder="https://example.com/my-tactic.jpg" />
                              </Form.Item>
                          );
                      }
                      
                      if (source === 'upload') {
                          return (
                              <Form.Item label="Upload Image">
                                  <Upload
                                      beforeUpload={(file) => {
                                          const reader = new FileReader();
                                          reader.onload = e => {
                                              if (e.target && e.target.result) {
                                                  setFieldsValue({ custom_image_url: e.target.result });
                                              }
                                          };
                                          reader.readAsDataURL(file);
                                          return false; // Prevent auto upload
                                      }}
                                      showUploadList={false}
                                  >
                                      <Button>Click to Upload Image</Button>
                                  </Upload>
                                  <Form.Item name="custom_image_url" noStyle>
                                      <Input type="hidden" />
                                  </Form.Item>
                                  {getFieldValue('custom_image_url') && <div style={{marginTop: 8, color: '#F59E0B'}}>Image Loaded Successfully</div>}
                              </Form.Item>
                          );
                      }
                      
                      return null;
                  }}
              </Form.Item>

              <Form.Item name="name" label="Tactic Name" rules={[{ required: true }]}>
                  <Input placeholder="e.g. Horns Flex Offense" />
              </Form.Item>

              <Row gutter={16}>
                  <Col span={12}>
                      <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                          <Select onChange={() => saveForm.setFieldsValue({ sub_category: null })}>
                              <Select.Option value="Offense">Offense</Select.Option>
                              <Select.Option value="Defense">Defense</Select.Option>
                              <Select.Option value="Strategy & Concepts">Strategy & Concepts</Select.Option>
                          </Select>
                      </Form.Item>
                  </Col>
                  <Col span={12}>
                      <Form.Item 
                          noStyle 
                          shouldUpdate={(prev, curr) => prev.category !== curr.category}
                      >
                          {({ getFieldValue }) => {
                              const category = getFieldValue('category');
                              let currentSubCategories: string[] = [];
                              if (category === 'Offense') {
                                  currentSubCategories = ['Set', 'Motion', 'Action', 'Continuity', 'Zone'];
                              } else if (category === 'Defense') {
                                  currentSubCategories = ['Man', 'Zone', 'Press'];
                              } else {
                                  currentSubCategories = ['General Strategy', 'Concept', 'Lineup'];
                              }
                              
                              return (
                                  <Form.Item name="sub_category" label="Sub-Category" rules={[{ required: true }]}>
                                      <Select placeholder="Type">
                                          {currentSubCategories.map(sub => (
                                              <Select.Option key={sub} value={sub}>{sub}</Select.Option>
                                          ))}
                                      </Select>
                                  </Form.Item>
                              );
                          }}
                      </Form.Item>
                  </Col>
              </Row>

              <Form.Item name="description" label="Description">
                  <Input.TextArea rows={3} placeholder="Briefly describe the tactic..." />
              </Form.Item>

              <Form.Item name="tags" label="Tags">
                <Select
                  mode="multiple"
                  options={atomicActionTagOptions}
                  placeholder="Select atomic action (e.g. PnR_BH, Spot_Up, Cut)"
                  optionFilterProp="label"
                  showSearch
                />
              </Form.Item>

              <Form.Item label="External Links">
                  <Input.Group compact>
                      <Form.Item name={['external_links', 'article']} noStyle>
                          <Input style={{ width: '50%' }} prefix={<LinkOutlined />} placeholder="Article / Wikipedia URL" />
                      </Form.Item>
                      <Form.Item name={['external_links', 'video']} noStyle>
                          <Input style={{ width: '50%' }} prefix={<PlayCircleOutlined />} placeholder="Video URL" />
                      </Form.Item>
                  </Input.Group>
              </Form.Item>
          </Form>
        </Modal>
      </ConfigProvider>

      {/* AI Lineup Diagnostic Panel */}
      <LineupDiagnosticPanel
        isOpen={isDiagnosticOpen}
        onClose={() => setIsDiagnosticOpen(false)}
        boardPlayers={entities.filter(e => e.type === 'player') as PlayerType[]}
        boardActionFrames={frames.map((frame, index) => ({
          frameIndex: index,
          actions: index === currentFrameIndex ? actionsMap[viewMode] : frame.actionsMap[viewMode],
        }))}
        currentTacticName={currentTacticMetadata?.name}
        onLoadTactic={handleLoadTactic}
      />
    </div>
  );
};

export default TacticsBoard;












